import { useLayoutEffect } from "react";

import { observePasswordManagerSuppression } from "@common/lib/suppressPasswordManagers";

/**
 * Stamp password-manager "ignore" attributes on ordinary text inputs while the
 * authenticated app is mounted.
 *
 * Pass `enabled = routableUserId !== null` (the same discriminator app.tsx uses
 * to choose the route tree) so suppression runs ONLY in the authenticated tree.
 * The unauthenticated tree — login / signup / reset-password — is left alone so
 * password managers work there. In-app `type="password"` fields (and their
 * paired username fields) are skipped regardless (see `suppressPasswordManagers`).
 *
 * `useLayoutEffect`, not `useEffect`, is deliberate: on a soft sign-out
 * `routableUserId` goes null, which mounts the Login tree AND flips `enabled` to
 * false in the SAME commit. The MutationObserver flushes its records on a
 * microtask right after the commit — before a `useEffect` cleanup (which is
 * deferred past paint) would run — so with `useEffect` the still-connected
 * observer would stamp the freshly-mounted login email field and break its
 * autofill until a reload. A `useLayoutEffect` cleanup runs synchronously during
 * the commit, so `disconnect()` discards those queued records before they flush.
 */
export function useSuppressPasswordManagers(enabled: boolean): void {
    useLayoutEffect(() => {
        if (!enabled) return;
        return observePasswordManagerSuppression(document.body);
    }, [enabled]);
}
